// Master Apps · Login screen — Microsoft SSO + login local (admins) · split-screen com painel de marca
import { useState } from 'react';
import { IconArrow, IconLock, IconShield } from '@/components/icons';
import { NocMark, ThemeToggle } from '@/components/primitives';
import { byViewport, useViewport, type Viewport } from '@/hooks/use-viewport';
import type { ScreenProps } from '@/lib/theme';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  NOC_RED_DARK,
  numeroTabular,
  rotuloCaixaAlta,
} from '@/lib/theme';

interface BrandGlyphProps {
  type: string;
  cx: number;
  cy: number;
  s: number;
  color: string;
}

function BrandGlyph({ type, cx, cy, s, color }: Readonly<BrandGlyphProps>) {
  if (type === 'grid') {
    const g = s * 0.21,
      o = g * 0.72;
    return (
      <g fill={color}>
        {[
          [-o, -o],
          [o, -o],
          [-o, o],
          [o, o],
        ].map(([dx, dy]) => (
          <rect
            key={`${dx},${dy}`}
            x={cx + dx! - g / 2}
            y={cy + dy! - g / 2}
            width={g}
            height={g}
            rx={g * 0.3}
          />
        ))}
      </g>
    );
  }
  if (type === 'bolt') {
    const k = s / 38;
    return (
      <path
        d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 Z"
        fill={color}
        transform={`translate(${cx},${cy}) scale(${k}) translate(-12,-12)`}
      />
    );
  }
  if (type === 'lines') {
    const w = s * 0.5,
      h = s * 0.1,
      gap = s * 0.18;
    return (
      <g fill={color}>
        {[0, 1, 2].map((i: number) => (
          <rect
            key={i}
            x={cx - w / 2}
            y={cy - h / 2 + (i - 1) * gap}
            width={i === 1 ? w * 0.7 : w}
            height={h}
            rx={h / 2}
          />
        ))}
      </g>
    );
  }
  if (type === 'layers') {
    const k = s / 40;
    return (
      <g
        fill="none"
        stroke={color}
        strokeWidth={1.9}
        strokeLinejoin="round"
        transform={`translate(${cx},${cy}) scale(${k}) translate(-12,-12)`}
      >
        <polygon points="12 3 3 8 12 13 21 8 12 3" />
        <polyline points="3 13 12 18 21 13" />
      </g>
    );
  }
  // ring + dot
  return (
    <g>
      <circle cx={cx} cy={cy} r={s * 0.22} fill="none" stroke={color} strokeWidth={s * 0.07} />
      <circle cx={cx} cy={cy} r={s * 0.07} fill={color} />
    </g>
  );
}

interface MabBrandPanelProps {
  vp: Viewport;
  accent?: string;
  accentDark?: string;
}

function MabBrandPanel({
  vp,
  accent = NOC_RED,
  accentDark = NOC_RED_DARK,
}: Readonly<MabBrandPanelProps>) {
  // Constelação de app-tiles — em destaque no fluxo do painel, logo abaixo do
  // card do logo/wordmark. Tiles hero são brancas sólidas com glyph escuro.
  const nodes = [
    { x: 204, y: 60, s: 52, hero: true, glyph: 'bolt' },
    { x: 72, y: 42, s: 28, hero: false, glyph: 'ring' },
    { x: 330, y: 48, s: 38, hero: false, glyph: 'grid' },
    { x: 96, y: 128, s: 46, hero: true, glyph: 'layers' },
    { x: 306, y: 132, s: 34, hero: false, glyph: 'lines' },
    { x: 384, y: 96, s: 24, hero: false, glyph: 'ring' },
    { x: 222, y: 186, s: 44, hero: true, glyph: 'grid' },
    { x: 52, y: 206, s: 26, hero: false, glyph: 'ring' },
    { x: 352, y: 200, s: 30, hero: false, glyph: 'lines' },
  ];
  const links = [
    [0, 1],
    [0, 2],
    [0, 3],
    [0, 4],
    [1, 3],
    [2, 5],
    [4, 5],
    [3, 6],
    [4, 6],
    [3, 7],
    [6, 7],
    [6, 8],
  ];

  // A constelação é DECORAÇÃO, então é a primeira coisa a ceder espaço: no
  // celular ela sai de cena (com ela no fluxo, o botão de SSO — o único objetivo
  // desta tela — caía abaixo da dobra em 375px), no tablet encolhe escalando o
  // MESMO viewBox, e no desktop fica intacta. `null` = não desenha.
  const constelacao = byViewport(vp, {
    mobile: null,
    tablet: { w: 306, h: 180 },
    desktop: { w: 408, h: 240 },
  });

  return (
    <div
      style={{
        // Empilhado: no compacto o painel virou a FAIXA de topo (largura cheia,
        // altura pelo conteúdo) em vez da coluna fixa de 520px do desktop.
        width: byViewport(vp, { mobile: '100%', desktop: '520px' }),
        flexShrink: 0,
        background: `
        radial-gradient(78% 56% at 84% 4%, rgba(255,255,255,0.20), rgba(255,255,255,0) 60%),
        radial-gradient(64% 52% at 8% 100%, rgba(0,0,0,0.30), rgba(0,0,0,0) 62%),
        linear-gradient(158deg, ${accent} 0%, ${accentDark} 54%, #560A15 100%)`,
        color: '#fff',
        padding: byViewport(vp, {
          mobile: '18px 20px 20px',
          tablet: '34px 36px 30px',
          desktop: '52px 56px',
        }),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: fontSans,
      }}
    >
      {/* malha de pontos refinada */}
      <svg style={{ position: 'absolute', inset: 0, opacity: 0.12 }} width="100%" height="100%">
        <defs>
          <pattern id="login-dots" width="26" height="26" patternUnits="userSpaceOnUse">
            <circle cx="1.4" cy="1.4" r="1.3" fill="#fff" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#login-dots)" />
      </svg>

      <div style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: byViewport(vp, { mobile: 44, desktop: 52 }),
              height: byViewport(vp, { mobile: 44, desktop: 52 }),
              borderRadius: 12,
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
              flexShrink: 0,
            }}
          >
            <NocMark size={byViewport(vp, { mobile: 34, desktop: 40 })} />
          </div>
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: '-0.01em',
              }}
            >
              Master<span style={{ opacity: 0.6 }}>·</span>Apps
            </div>
            {/* Assinatura em caixa alta espaçada: some no celular. Em 375px ela
                quebrava em três linhas de 10px e ainda passava por baixo do
                alternador de tema, que flutua neste canto. O wordmark acima já
                identifica o produto. */}
            {!vp.mobile && (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.18em',
                  opacity: 0.7,
                  marginTop: 2,
                  textTransform: 'uppercase',
                }}
              >
                Inventário de aplicações · Masterboi
              </div>
            )}
          </div>
        </div>

        {/* Constelação de app-tiles · em destaque no fluxo do painel, abaixo do logo */}
        {constelacao && (
          <svg
            style={{
              display: 'block',
              marginTop: 30,
              maxWidth: '100%',
              overflow: 'visible',
              pointerEvents: 'none',
            }}
            width={constelacao.w}
            height={constelacao.h}
            viewBox="0 0 408 240"
          >
            <defs>
              <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#fff" stopOpacity="0.55" />
                <stop offset="55%" stopColor="#fff" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#fff" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="linkGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#fff" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#fff" stopOpacity="0.12" />
              </linearGradient>
            </defs>

            {/* arestas de conexão */}
            <g stroke="url(#linkGrad)" strokeWidth="1.4">
              {links.map(([a, b], i: number) => (
                <line
                  key={i}
                  x1={nodes[a!]!.x}
                  y1={nodes[a!]!.y}
                  x2={nodes[b!]!.x}
                  y2={nodes[b!]!.y}
                />
              ))}
            </g>
            {/* faíscas nas pontas das arestas */}
            <g fill="#fff" opacity="0.5">
              {links.map(([, b], i: number) => (
                <circle key={i} cx={nodes[b!]!.x} cy={nodes[b!]!.y} r="2" />
              ))}
            </g>

            {/* brilhos difusos atrás das tiles hero */}
            {nodes
              .filter((n) => n.hero)
              .map((n) => (
                <circle
                  key={`${n.x},${n.y}`}
                  cx={n.x}
                  cy={n.y}
                  r={n.s * 1.7}
                  fill="url(#nodeGlow)"
                />
              ))}

            {/* tiles de apps */}
            {nodes.map((n) => (
              <g key={`${n.x},${n.y}`}>
                {n.hero ? (
                  <>
                    <rect
                      x={n.x - n.s / 2}
                      y={n.y - n.s / 2}
                      width={n.s}
                      height={n.s}
                      rx={n.s * 0.28}
                      fill="#fff"
                      opacity="0.96"
                    />
                    <BrandGlyph type={n.glyph} cx={n.x} cy={n.y} s={n.s} color={accentDark} />
                  </>
                ) : (
                  <>
                    <rect
                      x={n.x - n.s / 2}
                      y={n.y - n.s / 2}
                      width={n.s}
                      height={n.s}
                      rx={n.s * 0.28}
                      fill="#fff"
                      fillOpacity="0.12"
                      stroke="#fff"
                      strokeOpacity="0.55"
                      strokeWidth="1.2"
                    />
                    <BrandGlyph
                      type={n.glyph}
                      cx={n.x}
                      cy={n.y}
                      s={n.s}
                      color="rgba(255,255,255,0.92)"
                    />
                  </>
                )}
              </g>
            ))}
          </svg>
        )}
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          marginTop: 'auto',
          paddingTop: byViewport(vp, { mobile: 16, tablet: 24, desktop: 32 }),
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            opacity: 0.75,
            marginBottom: byViewport(vp, { mobile: 12, desktop: 18 }),
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#86efac',
              boxShadow: '0 0 0 4px #86efac33',
              animation: 'noc-pulse 1.6s ease-in-out infinite',
            }}
          />
          {'Inventário corporativo · ao vivo'}
        </div>
        <div
          style={{
            fontSize: byViewport(vp, { mobile: 22, tablet: 28, desktop: 34 }),
            fontWeight: 600,
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
            marginBottom: byViewport(vp, { mobile: 0, desktop: 18 }),
          }}
        >
          Todas as apps
          <br />
          em um só lugar.
        </div>
        {/* A descrição longa fica fora do celular: são três linhas de texto de
            marketing entre o wordmark e o botão de SSO. A coluna de acesso logo
            abaixo já explica o que fazer aqui. */}
        {!vp.mobile && (
          <div
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              opacity: 0.85,
              maxWidth: 360,
            }}
          >
            Catálogo, documentação e auditoria das aplicações da Masterboi, do Power Apps de chão de
            fábrica à API de rastreabilidade.
          </div>
        )}
      </div>

      {/* Versão e domínio: no celular sairiam repetidos a poucos pixels do
          rodapé da coluna de acesso, que traz os dois. */}
      {!vp.mobile && (
        <div
          style={{
            ...numeroTabular,
            position: 'relative',
            zIndex: 2,
            marginTop: 28,
            fontSize: 11,
            opacity: 0.6,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>Master Apps · v1.1.0</span>
          <span>solucoes.masterbbr.com</span>
        </div>
      )}
    </div>
  );
}

export function ScreenLogin({
  dark,
  accent = NOC_RED,
  accentDark = NOC_RED_DARK,
  onSignIn,
}: ScreenProps & { onSignIn: () => void }) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [hover, setHover] = useState(false);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        display: 'flex',
        // Abaixo de 1024px as duas colunas EMPILHAM: a de acesso tinha 248px em
        // 768px (medido) e o cartão de SSO não caberia nunca.
        flexDirection: byViewport(vp, { mobile: 'column', desktop: 'row' }),
        // A rota monta dentro de um wrapper de 100vh com overflow hidden. Quando
        // o conteúdo empilhado passa da altura da tela, quem precisa rolar é
        // ESTA caixa — senão o botão de SSO fica inalcançável no celular.
        overflowY: byViewport(vp, { mobile: 'auto', desktop: 'visible' }),
        // Âncora do alternador de tema, que passou a flutuar sobre a tela toda.
        // No desktop o canto da coluna de acesso É o canto da tela, então a
        // posição não muda; no compacto ele fica sobre a faixa de marca.
        position: 'relative',
        background: p.bg,
      }}
    >
      <MabBrandPanel vp={vp} accent={accent} accentDark={accentDark} />

      {/* Toggle de tema — canto superior direito da tela */}
      <div style={{ position: 'absolute', top: 18, right: 20, zIndex: 3 }}>
        <ThemeToggle />
      </div>

      <div
        style={{
          // Empilhado, a coluna não pode ENCOLHER abaixo do próprio conteúdo
          // (ela tem overflow hidden, o que zeraria a altura mínima automática e
          // cortaria o cartão). No desktop, `1 1 0%` é o mesmo que `flex: 1`.
          flex: byViewport(vp, { mobile: '1 0 auto', desktop: '1 1 0%' }),
          display: 'flex',
          flexDirection: 'column',
          background: p.bg,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            // O brilho é maior que a viewport do celular e vazava 100px de cada
            // lado (rolagem horizontal acidental medida em 320px e 375px).
            width: byViewport(vp, { mobile: 300, tablet: 460, desktop: 520 }),
            height: byViewport(vp, { mobile: 300, tablet: 460, desktop: 520 }),
            borderRadius: '50%',
            background: `radial-gradient(circle, ${accent}18 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        {/* padding-top compensa a altura do rodapé para manter o card centralizado */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: byViewport(vp, {
              mobile: '18px 16px 6px',
              tablet: '30px 40px 6px',
              desktop: '45px 56px 0',
            }),
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div style={{ width: '100%', maxWidth: 420 }}>
            <div
              style={{
                marginBottom: byViewport(vp, { mobile: 22, desktop: 32 }),
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: accent,
                  marginBottom: byViewport(vp, { mobile: 10, desktop: 14 }),
                }}
              >
                Acessar Master Apps
              </div>
              <div
                style={{
                  fontSize: byViewport(vp, { mobile: 24, desktop: 30 }),
                  fontWeight: 600,
                  color: p.text,
                  letterSpacing: '-0.022em',
                  lineHeight: 1.15,
                  marginBottom: 10,
                }}
              >
                Bem-vindo de volta.
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: p.muted,
                  lineHeight: 1.55,
                  maxWidth: 320,
                  margin: '0 auto',
                }}
              >
                Entre com sua conta corporativa Masterboi para acessar o catálogo completo.
              </div>
            </div>

            <div
              style={{
                position: 'relative',
                padding: 4,
                borderRadius: 16,
                background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
                boxShadow: hover
                  ? `0 24px 60px -12px ${accent}55, 0 0 0 1px ${accent}33`
                  : `0 12px 32px -8px ${accent}33, 0 0 0 1px ${accent}22`,
                transition: 'box-shadow 200ms, transform 200ms',
                transform: hover ? 'translateY(-2px)' : 'translateY(0)',
              }}
            >
              <div
                style={{
                  ...rotuloCaixaAlta,
                  position: 'absolute',
                  top: -10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#0a0a0b',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  padding: '4px 12px',
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                }}
              >
                <IconShield size={10} />
                SSO Corporativo
              </div>

              <button
                type="button"
                onClick={onSignIn}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={{
                  width: '100%',
                  // O alvo continua muito acima dos 44px de toque mesmo no
                  // celular: 46px de ícone + respiro dá ~78px de altura.
                  padding: byViewport(vp, {
                    mobile: '16px 14px',
                    tablet: '20px 22px',
                    desktop: '24px 28px',
                  }),
                  background: dark ? '#16171b' : '#fff',
                  color: p.text,
                  border: 'none',
                  borderRadius: 12,
                  fontFamily: fontSans,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: byViewport(vp, { mobile: 12, desktop: 16 }),
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    width: byViewport(vp, { mobile: 46, desktop: 56 }),
                    height: byViewport(vp, { mobile: 46, desktop: 56 }),
                    borderRadius: 10,
                    background: dark ? '#0a0a0b' : '#fafaf9',
                    border: `1px solid ${p.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="28" height="28" viewBox="0 0 23 23" aria-hidden="true">
                    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                    <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                    <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                    <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                  </svg>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: byViewport(vp, { mobile: 15, desktop: 16 }),
                      fontWeight: 600,
                      color: p.text,
                      letterSpacing: '-0.012em',
                    }}
                  >
                    Entrar com a Microsoft
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: p.muted,
                      marginTop: 3,
                      // Mono: é o domínio do e-mail de login, identificador.
                      fontFamily: fontMono,
                    }}
                  >
                    @masterboi.com.br
                  </div>
                </div>

                <div
                  style={{
                    width: byViewport(vp, { mobile: 32, desktop: 36 }),
                    height: byViewport(vp, { mobile: 32, desktop: 36 }),
                    borderRadius: 8,
                    background: accent,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transform: hover ? 'translateX(2px)' : 'translateX(0)',
                    transition: 'transform 200ms',
                  }}
                >
                  <IconArrow size={16} />
                </div>
              </button>
            </div>

            <div
              style={{
                marginTop: byViewport(vp, { mobile: 18, desktop: 28 }),
                padding: 14,
                background: p.surface2,
                border: `1px solid ${p.border}`,
                borderRadius: 8,
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ color: accent, marginTop: 1, flexShrink: 0 }}>
                <IconLock size={15} />
              </div>
              <div style={{ fontSize: 11, color: p.textMuted, lineHeight: 1.55 }}>
                Acesso restrito a colaboradores Masterboi. Todo o conteúdo do catálogo (apps
                públicas, privadas e internas) exige autenticação via Azure AD. Sessões são
                registradas e auditadas.
              </div>
            </div>

            <div
              style={{
                marginTop: byViewport(vp, { mobile: 12, desktop: 18 }),
                textAlign: 'center',
                fontSize: 12,
                color: p.muted,
              }}
            >
              Não consegue entrar?{' '}
              <a
                href="https://masterboi.sensrit.com.br"
                target="_blank"
                rel="noreferrer"
                style={{
                  color: accent,
                  textDecoration: 'none',
                  fontWeight: 600,
                  // No celular o link é o plano B de quem não consegue entrar, e
                  // como texto corrido dentro da frase ele quebrava em duas
                  // linhas de 31px de largura. Vira um alvo de toque inteiro
                  // (44px de altura, sem quebrar no meio).
                  display: byViewport(vp, { mobile: 'inline-flex', desktop: 'inline' }),
                  alignItems: 'center',
                  minHeight: byViewport(vp, { mobile: 44, desktop: 0 }),
                  padding: byViewport(vp, { mobile: '0 6px', desktop: '0' }),
                  whiteSpace: byViewport(vp, { mobile: 'nowrap', desktop: 'normal' }),
                }}
              >
                Falar com TI
              </a>
            </div>
          </div>
        </div>

        <div
          style={{
            ...numeroTabular,
            padding: byViewport(vp, { mobile: '12px 16px', desktop: '16px 32px' }),
            borderTop: `1px solid ${p.border}`,
            display: 'flex',
            // Lado a lado, as duas linhas somam 230px e se sobrepunham em 320px
            // (medido na mono, a sans é mais estreita): no celular viram duas
            // linhas centradas.
            flexDirection: byViewport(vp, { mobile: 'column', desktop: 'row' }),
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: byViewport(vp, { mobile: 3, desktop: 0 }),
            textAlign: 'center',
            fontSize: 11,
            color: p.muted,
            flexShrink: 0,
            position: 'relative',
            zIndex: 2,
          }}
        >
          <span>© 2026 Masterboi · Master Apps v1.1.0</span>
          <span>solucoes.masterbbr.com</span>
        </div>
      </div>
    </div>
  );
}
